import { type ApprovalStatus } from '@webapp/components/ApprovalStatusLabel/ApprovalStatusLabel'
import { CurrencyNames } from '@webapp/core/entities/external/Currency'
import {
  type PayCard,
  type PayCardholder,
  type SpendingCardCard,
  type User,
} from '@webapp/models'
import {
  type PayCardStatus,
  PayCardStatuses,
  PayRequestStatusEnum,
} from '@webapp/models/enums'
import { type PayRequest } from '@webapp/models/PayRequest'
import {
  Card,
  CardholderAccountStatuses,
  type CardStatusEnum,
  CardStatuses,
  CardTypes,
  DetailedCard,
  IssuingProviders,
} from '@webapp/modules/spending-cards'

export const formatCardExpiryDate = (expiryDate: string) => {
  if (expiryDate?.length !== 4) return ''

  // Date is formatted MMYY from Pay Card API
  return expiryDate.slice(0, 2).concat('/').concat(expiryDate.slice(2, 4))
}

export const removeWhiteSpace = (val: string) => {
  return val.replace(/\u2000/g, '').replace(/ /g, '')
}

export const convertCentsToDollars = (cents: number) => {
  return cents / 100
}

export const convertDollarsToCents = (dollars: number) => {
  return Number((dollars * 100).toFixed())
}

export const convertUserToCardholder = (user: User): PayCardholder => {
  return {
    id: user.id,
    user_id: user.id,
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    profile_image: user.profile_image,
    department_id: user.department?.id,
    external_id: undefined,
  }
}

/**
 * Converts a Card entity's cardholder to a PayCardholder object.
 * Only used because CardholderLabel in FundRequestSFormSummary expects
 * snake cased variables. Remove when we are able to support camel case
 * in the Form Summary component specifically Cardholder and User Label components
 * @param card - The Card entity to convert.
 * @return A Partial<PayCardholder> object with the cardholder's details.
 **/
export const convertCardCardholderToCardholder = (
  card: Card
): Partial<PayCardholder> => {
  if (card instanceof Card) {
    return {
      first_name: card.cardholder.firstName,
      last_name: card.cardholder.lastName,
      user_id: card.cardholder.userId,
    } as PayCardholder
  }
}

export const getRequestableUser = (card: Card) => {
  return card?.cardholder?.userId
}

export const getApprovalStatus = (request: PayRequest): ApprovalStatus => {
  switch (request.status) {
    case PayRequestStatusEnum.APPROVED:
      return 'approved'
    case PayRequestStatusEnum.DENIED:
      return 'denied'
    case PayRequestStatusEnum.PENDING:
      return 'pending'
  }
}

// Helper function to convert legacy models to Card entity
// Remove when airwallex_issuing is fully implemented
export const mapLegacyCardToCard = (
  legacyCard: PayCard | SpendingCardCard | Card
): Card => {
  if (!legacyCard) return undefined

  if (legacyCard instanceof Card) {
    // If it's already a Card, return it as is
    return legacyCard
  } else {
    let cardholderId = null
    if (!!(legacyCard as PayCard)?.cardholder?.user_id) {
      cardholderId = (legacyCard as PayCard).cardholder.user_id
    } else if (!!(legacyCard as SpendingCardCard)?.cardholder?.user) {
      cardholderId = (legacyCard as SpendingCardCard).cardholder.user
    }

    // Map the legacy card structure to Card entity constructor interface
    const card = new Card({
      id: legacyCard.id,
      externalId: legacyCard.external_id,
      last4Digits: legacyCard.number,
      type: CardTypes.PHYSICAL, // Assuming all legacy cards are physical
      status: CardStatuses.ACTIVE, // Assuming the status is always ACTIVE for the new model
      cardholder: {
        id: legacyCard.cardholder.id,
        firstName: legacyCard.cardholder.first_name,
        lastName: legacyCard.cardholder.last_name,
        profileImage: legacyCard.cardholder.profile_image,
        email: legacyCard.cardholder.email || '',
        secondaryCategoryId: legacyCard.cardholder.department_id || null,
        tosAccepted: legacyCard.cardholder.tos_accepted || true,
        externalId: legacyCard.cardholder.external_id,
        userId: cardholderId,
        provider: IssuingProviders.STRIPE,
      },
      createdAt: legacyCard.created_at,
      updatedAt: null,
      requireActivation: false, // Assuming this is always false for the new model
    })

    return card
  }
}

// Helper function to convert legacy models to DetailedCard entity
// Remove when airwallex_issuing is fully implemented
export const mapLegacyCardToDetailedCard = (
  legacyCard: PayCard | SpendingCardCard
): DetailedCard => {
  if (!legacyCard) return undefined

  let cardholderId = null
  if (!!(legacyCard as PayCard)?.cardholder?.user_id) {
    cardholderId = (legacyCard as PayCard).cardholder.user_id
  } else if (!!(legacyCard as SpendingCardCard)?.cardholder?.user) {
    cardholderId = (legacyCard as SpendingCardCard).cardholder.user
  }

  const updatedDate =
    (legacyCard as PayCard)?.updated_at ?? legacyCard.created_at // Use created_at as fallback
  const shippingAddress = (legacyCard as PayCard)?.shipping_address || null

  const payStatusToCardStatusMap: { [key in PayCardStatus]: CardStatusEnum } = {
    [PayCardStatuses.INACTIVE]: CardStatuses.INACTIVE,
    [PayCardStatuses.ACTIVE]: CardStatuses.ACTIVE,
    [PayCardStatuses.CANCELED]: CardStatuses.CANCELED,
    [PayCardStatuses.PENDING]: CardStatuses.INACTIVE, // Assuming PENDING maps to INACTIVE
  }

  // Map the legacy card structure to DetailedCard entity constructor interface
  const card = new DetailedCard({
    id: legacyCard.id,
    externalId: legacyCard.external_id,
    last4Digits: legacyCard.number,
    type: legacyCard.category,
    status: payStatusToCardStatusMap[legacyCard.status],
    cardholder: {
      id: legacyCard.cardholder.id,
      firstName: legacyCard.cardholder.first_name,
      lastName: legacyCard.cardholder.last_name,
      profileImage: legacyCard.cardholder.profile_image,
      email: legacyCard.cardholder.email || '',
      secondaryCategoryId: legacyCard.cardholder.department_id || null,
      tosAccepted: legacyCard.cardholder.tos_accepted || true,
      externalId: legacyCard.cardholder.external_id,
      userId: cardholderId,
      provider: IssuingProviders.STRIPE,
      status: CardholderAccountStatuses.ACTIVE, // Assuming all legacy cardholders are active
      billingAddress: {
        name: legacyCard.cardholder.billing_address?.name || '',
        addressLine1:
          legacyCard.cardholder.billing_address?.address_line_one || '',
        addressLine2:
          legacyCard.cardholder.billing_address?.address_line_two || '',
        postalCode: legacyCard.cardholder.billing_address?.postal_code || '',
        city: legacyCard.cardholder.billing_address?.city || '',
        stateProvince:
          legacyCard.cardholder.billing_address?.state_province || '',
        country: legacyCard.cardholder.billing_address?.country || '',
      },
    },
    createdAt: legacyCard.created_at,
    updatedAt: updatedDate,
    requireActivation: legacyCard.require_activation,
    balance: legacyCard.balance,
    currencyName: CurrencyNames.USD, // Assuming USD for Stripe cards
    shippingAddress: {
      name: shippingAddress?.name || '',
      addressLine1: shippingAddress?.address_line_one || '',
      addressLine2: shippingAddress?.address_line_two || '',
      postalCode: shippingAddress?.postal_code || '',
      city: shippingAddress?.city || '',
      stateProvince: shippingAddress?.state_province || '',
      country: shippingAddress?.country || '',
    },
  })

  return card
}
